import { CardElement, Elements, useElements } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { twMerge } from 'tailwind-merge';
import { match, P } from 'ts-pattern';
import Payments from '~/stores/payments';
import Field from './form-controls/Field';
import Icon from './Icon';

type PaymentFieldsProps = {
  className?: string;
  onChange?: (event: StripeCardElementChangeEvent) => void;
};
type CardState = Pick<
  StripeCardElementChangeEvent,
  'error' | 'complete' | 'empty'
>;

function CardFields({ onChange }: Pick<PaymentFieldsProps, 'onChange'>) {
  const elements = useElements();

  useEffect(() => {
    if (elements) {
      Payments.setElements(elements);
    }
  }, [elements]);

  return <CardElement onChange={onChange} />;
}

export default function PaymentFields({
  className,
  onChange,
}: PaymentFieldsProps) {
  const { stripeInstance, clientSecret } = useSyncExternalStore(
    Payments.subscribe,
    Payments.getSnapshot,
  );

  const [state, setState] = useState<CardState>({
    empty: true,
    complete: false,
    error: undefined,
  });

  const options = useMemo(() => ({ clientSecret }), [clientSecret]);

  const handleChange = (event: StripeCardElementChangeEvent) => {
    setState({
      error: event.error,
      complete: event.complete,
      empty: event.empty,
    });
    onChange?.(event);
  };

  return (
    <div
      data-testid="payment-fields"
      className={twMerge('flex w-full flex-col gap-2', className)}
    >
      <div
        className="w-full"
        // Sentry will block this element from rendering in session replays. The Stripe elements are already not capture since it is cross origin iframe.
        // This will hope have Stripe render a block to indicate that it was rendered.
        data-sentry-block="payment-fields"
      >
        <Elements stripe={stripeInstance} options={options}>
          <CardFields onChange={handleChange} />
        </Elements>
      </div>
      {match(state)
        .with({ error: P.nonNullable }, ({ error }) => (
          <Field.ErrorText>{error.message}</Field.ErrorText>
        ))
        .with({ complete: true }, () => (
          <Field.HelpText>
            <Icon icon="Check" className="mr-1" /> Payment information is
            complete.
          </Field.HelpText>
        ))
        .with({ complete: false }, () => (
          <Field.HelpText>
            Complete payment information to continue.
          </Field.HelpText>
        ))
        .exhaustive()}
    </div>
  );
}
